/**
 * Volba konfigurátoru jako karty s dopadem na cenu (KAN-1149).
 *
 * Rozbalovací seznam se ptá „vyber hodnotu"; karta se ptá „co chceš, aby díl
 * uměl", a rovnou říká, co to stojí. Rozdíl je vidět zrovna u volby, kvůli
 * které tenhle tiket vznikl: „Podpěry — žádné / odlamovací / rozpustné" je
 * pro zákazníka rozhodnutí o povrchu a ceně, ne výběr slova ze seznamu.
 *
 * **Nabídne se jen to, co dílna umí.** Každá hodnota se zkusí přes
 * ``/pricing/feasibility`` s TOUTO hodnotou mezi vstupy: co nemá schopný
 * stroj, se nezobrazí. Kapacitní bránu („nese stroj dva materiály najednou?")
 * uzavřel KAN-1137 + KAN-1139, kontrolu druhého materiálu v matici KAN-1138 —
 * tady se nic nezapíná, jen se to čte. Tenant, který takový stroj nemá, kartu
 * nikdy neuvidí a nemusí o tom vědět. **Ani na okamžik** (KAN-1495): karta
 * čeká na kladnou odpověď, místo aby se zobrazila a pak zmizela.
 *
 * **Cena je rozdíl náhledu, ne číslo z tabulky.** Karta ukazuje rozdíl mezi
 * cenou s touhle volbou a cenou s výchozí — přesně jak to žádá akceptace.
 * Počítá to týž engine, který dá výslednou cenu, takže se karta a košík
 * nemůžou rozejít.
 *
 * **Až po hlavní ceně (KAN-1642).** Srovnání voleb je N× proveditelnost
 * a N× cena; puštěné naráz s hlavní cenou se s ní na serveru praly a cena,
 * na kterou zákazník čeká, přišla poslední. Konfigurátor proto kartám řekne
 * ``deferLivePricing``, dokud hlavní cena nedorazí, a dotazy jdou přes
 * sdílené ``linePriceQuery`` / ``feasibilityQuery`` až pro ustálený stav.
 * Nabídka se tím nemění — jen pořadí, v jakém se na ni server ptá.
 *
 * **Genericita.** Platforma zná jen „volba s popsanými hodnotami". Že jde
 * o podpěry, o druhou nit u výšivky nebo o druhou komponentu 2K, je autoring
 * operace — komponenta nezná ani jeden z těch pojmů.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { optionCompareQuery, useSettled } from "@/api/queries/pricing";
import { minorToMajor } from "@/lib/money";
import { cx } from "@/lib/utils";

import { dimsFromAnalysis } from "./useCustomerConfiguratorState";

import type { AnalysisSnapshot } from "@/api/types";

/** Jedna popsaná hodnota volby, jak ji autoruje schéma operace. */
export interface ChoiceOption {
  value: string;
  label: string;
  /** Jedna věta, proč si to vybrat. Prázdné → karta nese jen jméno. */
  description?: string | null;
}

interface Props {
  fieldKey: string;
  label: string;
  description?: string;
  options: ChoiceOption[];
  value: unknown;
  onChange: (next: string) => void;
  /** Celý stav konfigurátoru — vstup proveditelnosti i ceny. */
  config: Record<string, unknown>;
  processType: string;
  analysis?: AnalysisSnapshot | null;
  /** Náhled v editoru schémat: bez katalogu se nedá nic zkoušet ani cenit. */
  disabled?: boolean;
  /** ``false`` = karty jsou klikatelné, ale na proveditelnost ani cenu se
   *  neptají. Platformní ukázka (landing, KAN-1561) nemá tenanta: dotaz na
   *  ``/pricing/*`` by skončil 422, nebo by přihlášenému návštěvníkovi
   *  nacenil jeho vlastní obchod. Cenu tam dává výhradně veřejný endpoint. */
  livePricing?: boolean;
  /** ``true`` = na srovnání se zatím neptej — hlavní cena ještě nedorazila
   *  (KAN-1642). Karty se chovají jako během načítání: nabídne se jen
   *  vybraná hodnota, dokud server neřekne, co dál jde. */
  deferLivePricing?: boolean;
  testIdPrefix?: string;
}

export function ChoiceCards({
  fieldKey,
  label,
  description,
  options,
  value,
  onChange,
  config,
  processType,
  analysis,
  disabled = false,
  livePricing = true,
  deferLivePricing = false,
  testIdPrefix = "cfg-choice",
}: Props): JSX.Element {
  const { t } = useTranslation("quote");
  const materialCode =
    typeof config.material_code === "string" ? config.material_code : "";
  const dims = useMemo(() => dimsFromAnalysis(analysis), [analysis]);
  // Výchozí hodnota je základ, proti kterému se rozdíl počítá — ne nejlevnější
  // z nabídky. „O 180 Kč dráž než to, co bys dostal jinak" je otázka, na kterou
  // zákazník čeká; „o 180 Kč dráž než nejlevnější varianta" ne.
  const baseValue = options[0]?.value ?? "";
  // Bez materiálu (a v náhledu) se neptáme na nic: proveditelnost i cena by
  // byly dotaz bez zadání a odpověď „nejde to" by lhala.
  const live =
    livePricing && !disabled && Boolean(materialCode) && Boolean(processType);

  // Na co se karty ptají — jeden objekt, aby se ustálil celý najednou.
  const { settled: ask } = useSettled(
    live && !deferLivePricing
      ? { config, materialCode, analysis }
      : null,
  );

  // KAN-1786 — všechny hodnoty volby jedním voláním (server zkusí každou
  // týmiž funkcemi jako /pricing/feasibility a /pricing/line), ne N+N dotazy.
  const baseConfig = ask?.config ?? config;
  const compare = useQuery({
    ...optionCompareQuery({
      process_type: processType,
      field_key: fieldKey,
      values: options.map((o) => o.value),
      line: {
        analysis: ask?.analysis ?? analysis,
        material_code: ask?.materialCode ?? materialCode,
        quantity: typeof baseConfig.quantity === "number" ? baseConfig.quantity : 1,
        extra_config: baseConfig,
      } as never,
      feasibility: {
        process_type: processType,
        dim_x_mm: dims.dim_x_mm ?? null,
        dim_y_mm: dims.dim_y_mm ?? null,
        dim_z_mm: dims.dim_z_mm ?? null,
        material_code: ask?.materialCode ?? materialCode,
        inputs: baseConfig,
      },
    }),
    enabled: ask !== null,
  });
  const answerOf = (value: string) => compare.data?.items.find((it) => it.value === value);

  const unitMinorAt = (i: number): number | null => {
    const minor = answerOf(options[i]?.value ?? "")?.unit_price_minor;
    return typeof minor === "number" ? minor : null;
  };
  const baseIndex = options.findIndex((o) => o.value === baseValue);
  const baseMinor = baseIndex >= 0 ? unitMinorAt(baseIndex) : null;

  // Nabídne se hodnota, o které SERVER řekl, že jde — ne hodnota, o které
  // se ještě neví.
  //
  // KAN-1149 to mělo obráceně: dokud odpověď nedorazila, karta zůstala, s
  // odůvodněním „mizející nabídka je horší než nabídka, která se po kliknutí
  // ohradí". KAN-1495 tu úvahu otočil měřením: mezi dokončením analýzy a
  // doběhnutím ceny byla volba DRUHÉHO materiálu zakázky chvíli klikatelná
  // a pak zmizela, na stroji, který dva materiály najednou neunese. Zákazník
  // tedy stejně viděl nabídku mizet — jen až potom, co si ji stihl vybrat.
  // Akceptace: „Nedostupná hodnota se nenabízí ani během načítání."
  //
  // Tři případy, kdy karta zůstane i bez kladné odpovědi:
  //
  // * **Neptáme se** (``live`` je false — náhled editoru, konfigurátor bez
  //   materiálu). Nikdo nic neodmítl, takže není co schovávat; tohle drží
  //   i původní test „dokud odpověď nedorazí, karta zůstane".
  // * **Dotaz spadl.** Výpadek není verdikt. Skrýt nabídku kvůli 500 by z
  //   chyby sítě udělalo tvrzení o dílně.
  // * 🪤 **Právě vybraná hodnota**, vždycky — i když se stane neproveditelnou
  //   (zákazník mezitím přepnul materiál). Zmizela by z očí, ale ne z
  //   konfigurace, a košík by pak odmítl volbu, kterou není kde vidět ani
  //   změnit.
  const visible = options
    .map((opt, i) => ({ opt, i }))
    .filter(({ opt }) => {
      if (opt.value === value || !live) return true;
      if (compare.isError) return true;
      const answer = answerOf(opt.value);
      // ``null`` = proveditelnost selhala, což není verdikt.
      return answer !== undefined && answer.orderable !== false;
    });

  return (
    <div className="space-y-1.5" data-testid={testIdPrefix}>
      <span className="block text-xs font-medium text-ink-2">{label}</span>
      {description && <p className="text-[11px] text-ink-3">{description}</p>}
      <div
        role="radiogroup"
        aria-label={label}
        className="flex flex-wrap gap-2"
      >
        {visible.map(({ opt, i }) => {
          const on = value === opt.value;
          const minor = unitMinorAt(i);
          const delta =
            baseMinor !== null && minor !== null ? minor - baseMinor : null;
          const currency = answerOf(opt.value)?.currency_code ?? null;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={disabled}
              onClick={() => onChange(opt.value)}
              data-testid={`${testIdPrefix}-${opt.value}`}
              className={cx(
                "min-w-[140px] flex-1 rounded-lg border p-2.5 text-left transition-colors disabled:opacity-60",
                on
                  ? "border-accent bg-accent/5"
                  : "border-border hover:bg-bg",
              )}
            >
              <span className="block text-[13px] font-medium text-ink">
                {opt.label}
              </span>
              {opt.description && (
                <span className="block text-[11px] text-ink-3">
                  {opt.description}
                </span>
              )}
              {delta !== null && delta !== 0 && (
                <span
                  className="mt-0.5 block text-[11px] text-ink-2"
                  data-testid={`${testIdPrefix}-${opt.value}-delta`}
                >
                  {t("configurator.choiceFrom", {
                    defaultValue: "od {{sign}}{{amount}} {{currency}}",
                    sign: delta > 0 ? "+" : "−",
                    amount: Math.abs(minorToMajor(delta, currency)),
                    currency: currency ?? "",
                  })}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
